import { formatNumber, toMoney, toPercent } from '@utils/Format'
import { PLANOS } from '@modules/ai-framework/constants/copy'
import {
  CHECKOUT_QUERY_PARAM,
  CHECKOUT_CANCELADO,
} from '@modules/ai-framework/constants/Text'
import { CASAS_EXIBICAO, formatCreditosExibicao } from '@modules/ai-framework/utils/creditos'

// View-model de UM card de plano (spec-005, [T027] — US-1).
//
// ★ POR QUE ISTO É UM UTIL, E NÃO MÉTODOS DO COMPONENTE
//
// Duas razões, nesta ordem: a regra que mora aqui é a mais cara da tela — **o plano anual
// mostra créditos POR MÊS, nunca o total do período** — e ela merece um teste que não
// dependa de montar Vue, de store e de shallowMount; e o SFC passava de 500 linhas com ela
// dentro (teto do repo). O nome é greppável de propósito: quem for "melhorar" o card
// anual multiplicando por `ciclos_por_periodo` esbarra no teste deste arquivo.
//
// ★ NADA É DERIVADO AQUI. Preço, créditos, bônus, equivalente mensal e economia anual vêm
// calculados do servidor (`contracts/api.md §1`). Este módulo só FORMATA — e a única
// aritmética que ele faz é centavos → reais, que é conversão de unidade, não de valor.
//
// `CASAS_EXIBICAO` vem de `utils/creditos` (S23, review PR #2211): é a fonte única da
// regra "duas casas na exibição", compartilhada com o ledger/extrato/log de execuções —
// redeclará-la aqui deixava as duas cópias livres para divergir sozinhas.
const CENTAVOS_POR_REAL = 100
const PERIODICIDADE_ANUAL = 'anual'
const PERIODICIDADE_MENSAL = 'mensal'
const SEM_CASAS = 0
const STATUS_AGUARDANDO = 'aguardando_pagamento'

// `null`/`undefined`/não-numérico devolvem string vazia — o template esconde a linha em
// vez de exibir "R$ 0,00", que se leria como plano de graça.
function moedaDeCentavos(centavos) {
  if (centavos === null || centavos === undefined || !isFinite(Number(centavos))) {
    return ''
  }
  return toMoney(Number(centavos) / CENTAVOS_POR_REAL, CASAS_EXIBICAO)
}

function percentualNumero(bruto) {
  const numero = Number(bruto)
  return isFinite(numero) ? numero : 0
}

// Percentual inteiro sai sem casas (`20%`); fracionário mantém as duas (`12,50%`).
// Arredondar 12,5 para 13 distorceria um número que o servidor calculou.
// ★ `toPercent` (M6, review PR #2211) já é `formatNumber(num, precision) + '%'` —
// `percentualNumero` garante um número finito, então a condição de `toPercent` sempre
// entra pelo ramo numérico. A regra de casas continua aqui: é ESTA função, não a de
// `@utils/Format`, que decide inteiro-sem-casas × fracionário-com-duas.
function percentualLabel(bruto) {
  const numero = percentualNumero(bruto)
  const casas = Number.isInteger(numero) ? SEM_CASAS : CASAS_EXIBICAO
  return toPercent(numero, casas)
}

// Créditos por ciclo NO CARD de plano: inteiro sai sem casas ("220"), fracionário mantém as
// duas ("220,50") — `contracts/ui-components.md §1` da spec-005, e o mesmo número que o
// bullet do backend escreve logo abaixo; antes a linha dizia "220,00" e o bullet "220".
// ★ Decide AQUI, no call-site, e não no `formatCreditosExibicao`: aquele é o formato do
// LEDGER (sempre duas casas, onde "0,43" importa) e é compartilhado pelo extrato, pelo log
// de execuções e pelo saldo — mudar o default dele mudaria essas telas junto.
function creditosDoCicloLabel(bruto) {
  const exibicao = formatCreditosExibicao(bruto)
  if (exibicao.naoDebitado) return ''
  const modulo = Math.abs(Number(bruto))
  if (!Number.isInteger(modulo)) return exibicao.moduloLabel
  return formatNumber(modulo, SEM_CASAS)
}

// ★ A HIERARQUIA DE PREÇO DO CARD ANUAL, em um lugar só.
//
// No anual, o DESTAQUE é o valor POR MÊS — é o número que se compara com o card mensal ao
// lado, e o que o consultor usa para decidir. O valor cheio do período não some: desce para
// a linha de cobrança ("R$ 4.500,00 cobrados anualmente"), porque é o que a Stripe cobra
// de fato e omiti-lo seria vender um preço que não é o da fatura.
//
// ★ Os DOIS números vêm do servidor (`valor_centavos` e `valor_mensal_equivalente_centavos`,
// `contracts/api.md §1`) — o que esta função escolhe é qual ocupa qual slot, nunca quanto
// vale. Anual sem o equivalente (catálogo em transição) cai no comportamento do cheio em
// destaque; dividir por 12 aqui para preencher o slot seria aritmética de dinheiro no
// cliente, a mesma classe de defeito que somar base + bônus.
function precoDoCard(plano) {
  const cheio = moedaDeCentavos(plano.valor_centavos)
  const ehAnual = plano.periodicidade === PERIODICIDADE_ANUAL
  const sufixoDoPeriodo = PLANOS.VALOR_SUFIXO[plano.periodicidade] || ''
  // ★ `sublinhaLabel` (cards, 18/09/2026) só afirma "Por mês" quando o número em destaque
  // É por mês: no anual sem inversão (abaixo) o destaque volta a ser o valor do ano, e a
  // sublinha fica vazia em vez de descrever a grandeza errada.
  const semInversao = {
    valorLabel: cheio,
    sufixo: sufixoDoPeriodo,
    cobrancaLabel: '',
    sublinhaLabel: ehAnual ? '' : PLANOS.SUBLINHA_MENSAL,
  }

  if (!ehAnual) return semInversao

  const mensal = moedaDeCentavos(plano.valor_mensal_equivalente_centavos)
  // Sem preço cheio não há o que dizer na linha de baixo, e o destaque mensal ficaria
  // solto — o card inteiro volta ao formato sem inversão.
  if (!mensal || !cheio) return semInversao

  return {
    valorLabel: mensal,
    // O sufixo é a unidade do número EXIBIDO, não a periodicidade do plano.
    sufixo: PLANOS.VALOR_SUFIXO[PERIODICIDADE_MENSAL] || '',
    cobrancaLabel: PLANOS.COBRADO_ANUALMENTE(cheio),
    sublinhaLabel: PLANOS.SUBLINHA_ANUAL(cheio),
  }
}

// A linha que só existe no plano ANUAL, e só quando o servidor mandou o número.
function economiaLabel(plano) {
  if (plano.periodicidade !== PERIODICIDADE_ANUAL) return ''
  const centavos = plano.economia_anual_centavos
  if (centavos === null || centavos === undefined) return ''
  return PLANOS.ECONOMIA_ANUAL(moedaDeCentavos(centavos))
}

// O estado que o `aria-label` soma ao preço e aos créditos (S9, review PR #2211): sem
// ele, quem usa leitor de tela não ouve por que um plano está fora de alcance nem qual já
// assina — a mesma informação que aparece em PALAVRA na tela (`PLANO_ATUAL`, `INDISPONIVEL`
// + `INDISPONIVEL_AJUDA`, a etiqueta comercial do backend). Prioridade, não soma: o plano
// vigente não precisa mais ser "vendido" pela etiqueta, e indisponibilidade não convive
// com "seu plano atual".
function estadoDoCard({ planoAtual, disponivel, etiqueta }) {
  if (planoAtual) return PLANOS.PLANO_ATUAL
  if (!disponivel) return `${PLANOS.INDISPONIVEL}: ${PLANOS.INDISPONIVEL_AJUDA}`
  return etiqueta || ''
}

// `{ codigoSelecionado, codigoVigente }` decidem os dois estados que não vêm do catálogo:
// o que o consultor acabou de clicar e o que ele já assina.
// "R$ 1.800,00 por ano · economia de R$ 600,00" — só no anual, com os dois números do
// servidor (`valor_centavos`, `economia_anual_centavos`); a economia some quando não vem.
function anoLinha(plano) {
  if (plano.periodicidade !== PERIODICIDADE_ANUAL) return ''
  const cheio = moedaDeCentavos(plano.valor_centavos)
  if (!cheio) return ''
  const economia = plano.economia_anual_centavos
  const temEconomia = economia !== null && economia !== undefined
  return PLANOS.ANO_LINHA(cheio, temEconomia ? moedaDeCentavos(economia) : '')
}

// "200 + 20 de bônus (10%)" — a decomposição do total, só quando há bônus. Os três
// números vêm do servidor; nada é somado aqui.
function composicaoLabel(plano) {
  if (percentualNumero(plano.bonus_percentual) <= 0) return ''
  const base = creditosDoCicloLabel(plano.creditos_base)
  const bonus = creditosDoCicloLabel(plano.creditos_bonus)
  if (!base || !bonus) return ''
  return PLANOS.COMPOSICAO_CREDITOS(base, bonus, percentualLabel(plano.bonus_percentual))
}

// ★ Os bullets do card SEM os que repetem a caixa de créditos. O backend publica
// `beneficios` nesta ordem (`usecase_creditos_planos._beneficios`): [créditos por mês
// (sempre), bônus em % (só com `bonus_percentual > 0`), desconto anual (só no anual),
// renovação, cancelamento, extras]. Os dois primeiros são os fatos que a caixa já mostra
// — cortados pela ORIGEM (os campos que os geram), não por comparação de texto.
function beneficiosDoCard(plano) {
  const beneficios = plano.beneficios || []
  const repetidos = percentualNumero(plano.bonus_percentual) > 0 ? 2 : 1
  return beneficios.slice(repetidos)
}

// ★ OS ITENS DO CARD (mockup do owner, 18/09/2026) — e por que NÃO são `beneficiosDoCard`.
//
// `beneficiosDoCard` devolve o que sobra dos `beneficios` do servidor depois de cortar os
// dois fatos que a caixa de créditos já mostrava: desconto anual, renovação e cancelamento.
// Essas três frases são IDÊNTICAS nos três planos — repeti-las em três cards lado a lado
// era a maior fonte de poluição da aba, e elas viraram UMA nota abaixo do catálogo
// (`PLANOS.NOTA_RODAPE_*`). O card passou a dizer o que aquele plano ENTREGA.
//
// A lista tem duas origens, e elas não se misturam:
// 1. o PRIMEIRO item é DERIVADO do catálogo (total + base + bônus + %), como sempre —
//    é a antiga caixa cinza virada item;
// 2. os demais são copy de produto por FAMÍLIA (`PLANOS.BENEFICIOS_POR_FAMILIA`), a única
//    coisa desta tela que não sai de um campo. Família desconhecida devolve só o item 1 —
//    nunca o texto de outro plano.
//
// `beneficiosDoCard` continua EXISTINDO e intocado: é o que a tabela da página
// (`creditos-planos-tabela`, coluna Condições) consome. Mudar a função compartilhada
// mudaria a página junto, e o pedido foi só sobre os cards do modal.
function itensDoCard(plano) {
  const creditos = creditosDoCicloLabel(plano.creditos_por_ciclo)
  const primeiro = creditos ? PLANOS.CREDITOS_ITEM(creditos, composicaoLabel(plano)) : ''
  const daFamilia = PLANOS.BENEFICIOS_POR_FAMILIA[plano.familia] || []
  return [primeiro].concat(daFamilia).filter(Boolean)
}

// ★ O VALOR POR MÊS que ordena os planos entre si (protótipo v2, "Fazer upgrade" × "Trocar
// para"). No mensal é o próprio preço; no anual é o `valor_mensal_equivalente_centavos` que
// o SERVIDOR calculou — nunca `valor_centavos / 12` aqui (aritmética de dinheiro no
// cliente). Sem o equivalente (catálogo em transição) devolve `null`: sem número não há
// ordem, e a ação cai em "Trocar para", que não promete direção.
function valorMensalEquivalente(plano) {
  if (!plano) return null
  const bruto =
    plano.periodicidade === PERIODICIDADE_ANUAL
      ? plano.valor_mensal_equivalente_centavos
      : plano.valor_centavos
  if (bruto === null || bruto === undefined || !isFinite(Number(bruto))) return null
  return Number(bruto)
}

function planoVigenteNoCatalogo(assinatura, planos) {
  if (!assinatura) return null
  return (planos || []).find((plano) => plano.codigo === assinatura.codigo_plano) || null
}

// O valor por mês do plano VIGENTE, lido do catálogo pelo `codigo_plano` da assinatura: a
// assinatura traz o preço cheio (`valor_centavos`) e a periodicidade, mas não o
// equivalente mensal do anual. Fallback só no mensal (o cheio É o mensal); anual fora do
// catálogo devolve `null`.
function valorMensalVigenteDe(assinatura, planos) {
  if (!assinatura) return null
  const doCatalogo = planoVigenteNoCatalogo(assinatura, planos)
  if (doCatalogo) return valorMensalEquivalente(doCatalogo)
  if (assinatura.periodicidade !== PERIODICIDADE_MENSAL) return null
  return valorMensalEquivalente(assinatura)
}

// A família do plano VIGENTE, pelo catálogo: a outra periodicidade da MESMA família é troca
// de ciclo, não de patamar — e o mensal custa MAIS por mês que o anual sem ser upgrade.
// Fora do catálogo, `null`: sem família não há o que comparar.
function familiaVigenteDe(assinatura, planos) {
  const doCatalogo = planoVigenteNoCatalogo(assinatura, planos)
  return (doCatalogo && doCatalogo.familia) || null
}

const ACAO_ATUAL = 'atual'
const ACAO_INDISPONIVEL = 'indisponivel'
const ACAO_UPGRADE = 'upgrade'
const ACAO_TROCAR = 'trocar'
const ACAO_ASSINAR = 'assinar'

// ★ A AÇÃO de cada plano diante do vigente — UMA regra para a tabela da página e para os
// cards do modal: `{ tipo, rotulo, rotuloContinuar, primary, disabled }`.
//
// - `atual`        → "Plano atual", desabilitado (reassinar o vigente é 409 no servidor);
// - `indisponivel` → "Indisponível no momento", desabilitado;
// - `upgrade`      → "Fazer upgrade" (`primary`): valor por mês MAIOR que o do vigente,
//                    em OUTRA família;
// - `trocar`       → "Trocar para {nome}" (`outline`): menor, igual, sem número para
//                    comparar, ou a mesma família noutra periodicidade — sem subir de
//                    patamar não se promete "upgrade";
// - `assinar`      → "Assinar {nome}" (`primary` só no `destaque`): ninguém assina ainda.
//
// `rotuloContinuar` é a versão do rodapé do modal ("Fazer upgrade para Pro" / "Trocar para
// Starter" / "Continuar com Growth").
function acaoDoPlano(
  plano,
  { codigoVigente = null, valorMensalVigente = null, familiaVigente = null } = {}
) {
  const planoAtual = Boolean(codigoVigente) && plano.codigo === codigoVigente
  if (planoAtual) {
    return { tipo: ACAO_ATUAL, rotulo: PLANOS.PLANO_ATUAL_CURTO, rotuloContinuar: '', primary: false, disabled: true }
  }
  if (plano.disponivel === false) {
    return { tipo: ACAO_INDISPONIVEL, rotulo: PLANOS.INDISPONIVEL_CURTO, rotuloContinuar: '', primary: false, disabled: true }
  }
  if (!codigoVigente) {
    return {
      tipo: ACAO_ASSINAR,
      rotulo: PLANOS.ASSINAR(plano.nome),
      rotuloContinuar: PLANOS.CONTINUAR(plano.nome),
      primary: Boolean(plano.destaque),
      disabled: false,
    }
  }
  const valorMensal = valorMensalEquivalente(plano)
  const mesmaFamilia = Boolean(familiaVigente) && plano.familia === familiaVigente
  const ehUpgrade =
    !mesmaFamilia &&
    valorMensal !== null &&
    valorMensalVigente !== null &&
    valorMensal > valorMensalVigente
  if (ehUpgrade) {
    return {
      tipo: ACAO_UPGRADE,
      rotulo: PLANOS.UPGRADE,
      rotuloContinuar: PLANOS.UPGRADE_CONTINUAR(plano.nome),
      primary: true,
      disabled: false,
    }
  }
  return {
    tipo: ACAO_TROCAR,
    rotulo: PLANOS.TROCAR(plano.nome),
    rotuloContinuar: PLANOS.TROCAR(plano.nome),
    primary: false,
    disabled: false,
  }
}

function montarCardDePlano(
  plano,
  { codigoSelecionado = null, codigoVigente = null, valorMensalVigente = null, familiaVigente = null } = {}
) {
  const preco = precoDoCard(plano)
  const creditos = creditosDoCicloLabel(plano.creditos_por_ciclo)
  const creditosLabel = creditos ? PLANOS.CREDITOS_POR_CICLO(creditos) : ''
  const planoAtual = Boolean(codigoVigente) && plano.codigo === codigoVigente
  const disponivel = plano.disponivel !== false

  return {
    // A ação do botão da linha/card diante do vigente (tabela e cards leem daqui).
    acao: acaoDoPlano(plano, { codigoVigente, valorMensalVigente, familiaVigente }),
    codigo: plano.codigo,
    nome: plano.nome,
    etiqueta: plano.etiqueta,
    destaque: Boolean(plano.destaque),
    valorLabel: preco.valorLabel,
    sufixo: preco.sufixo,
    cobrancaLabel: preco.cobrancaLabel,
    sublinhaLabel: preco.sublinhaLabel,
    economiaLabel: economiaLabel(plano),
    anoLinha: anoLinha(plano),
    composicaoLabel: composicaoLabel(plano),
    beneficiosDoCard: beneficiosDoCard(plano),
    // Os itens do CARD (o item 1 derivado + a copy da família). A tabela da página segue
    // lendo `beneficiosDoCard`, acima — as duas listas convivem de propósito.
    itensDoCard: itensDoCard(plano),
    // ★ `creditos_por_ciclo`, e NUNCA ele × `ciclos_por_periodo`: o anual entrega o mesmo
    // volume POR MÊS que o mensal, e cada parcela expira no fim do seu ciclo. "7.200
    // créditos" é um número plausível, errado, e que ninguém confere depois.
    creditosLabel: creditosLabel,
    // Valor exato como veio do backend — o arredondamento é só de exibição (ADR-007).
    creditosExato: String(plano.creditos_por_ciclo),
    temBonus: percentualNumero(plano.bonus_percentual) > 0,
    bonusLabel: PLANOS.BONUS_TAG(percentualLabel(plano.bonus_percentual)),
    beneficios: plano.beneficios || [],
    disponivel: disponivel,
    planoAtual: planoAtual,
    // Reassinar o plano vigente já se sabe que o servidor recusa com 409 — a tela não
    // oferece o clique que vai falhar, e diz qual é o plano atual.
    selecionavel: disponivel && !planoAtual,
    selecionado: Boolean(codigoSelecionado) && codigoSelecionado === plano.codigo,
    // O card é um <button> com `aria-label`: quem usa leitor de tela ouve ESTA string no
    // lugar do conteúdo. No anual ela fala os dois valores — o destaque por mês e o que
    // será efetivamente cobrado —, senão o preço da fatura sumiria para quem não vê a tela.
    // Os créditos vão com a unidade ("220 créditos por mês"): o número solto, lido em voz
    // alta depois de um preço, não diz de que grandeza é.
    ariaLabel: PLANOS.ARIA_PLANO(
      plano.nome,
      preco.cobrancaLabel
        ? preco.valorLabel + ', ' + preco.cobrancaLabel
        : preco.valorLabel,
      creditosLabel,
      estadoDoCard({ planoAtual, disponivel, etiqueta: plano.etiqueta })),
  }
}

// O marcador que o backend põe no `cancel_url` (`?checkout=cancelado`). Quem lê a URL é
// a VIEW, que passa o resultado como prop: os componentes não dependem do router.
function checkoutCanceladoNaUrl(query) {
  return Boolean(query) && query[CHECKOUT_QUERY_PARAM] === CHECKOUT_CANCELADO
}

// ★ Quem DESISTIU do checkout de assinatura (spec-005 T040, G-8 do QA). A Stripe devolve
// quem clica em "Voltar" para a mesma tela de quem pagou, e as duas linhas estão em
// `aguardando_pagamento`: sem o marcador do `cancel_url`, a tela dizia "estamos
// confirmando seu pagamento" a quem não pagou nada. Exige os DOIS sinais — o marcador
// sozinho (URL velha no histórico, assinatura já ativa) não pode rebaixar um plano pago.
// Um helper só porque dois componentes decidem por ele (o cartão e a vitrine).
function checkoutAbandonado(checkoutCancelado, assinatura) {
  if (!checkoutCancelado) return false
  return Boolean(assinatura) && assinatura.status_assinatura === STATUS_AGUARDANDO
}

export {
  ACAO_ASSINAR,
  ACAO_ATUAL,
  ACAO_INDISPONIVEL,
  ACAO_TROCAR,
  ACAO_UPGRADE,
  acaoDoPlano,
  checkoutAbandonado,
  checkoutCanceladoNaUrl,
  creditosDoCicloLabel,
  familiaVigenteDe,
  itensDoCard,
  montarCardDePlano,
  moedaDeCentavos,
  percentualLabel,
  valorMensalEquivalente,
  valorMensalVigenteDe,
  // O cartão da assinatura decide o polling por este valor, e o `checkoutAbandonado`
  // (acima) decide a desistência por ele: exportado para os dois concordarem por
  // construção (review #2211, S5).
  STATUS_AGUARDANDO,
}
